import { apiClient } from "@/api/client";
import type { NewLegalEntityFormType } from "@/components/units/LegalEntityCreateForm/types";
import { API_ENDPOINTS } from "@/config/app";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { buildUrl } from "@/utils/buildUrl";

import type {
  AssignableBrandsAndBranchesResponse,
  AssignableResponse,
  IDetailsLegalEntity,
  ILegalEntity,
  LegalEntitiesAvailableAsChildrenResponse,
  LegalSubPosition,
  LinkedLegalEntityPayload,
} from "./types";

export const legalEntityApi = {
  async getLegalEntity(
    params?: PaginationParams
  ): Promise<PaginatedResponse<ILegalEntity>> {
    const url = buildUrl(API_ENDPOINTS.ADMIN, "/legal-entities", params);

    const response = await apiClient.get<PaginatedResponse<ILegalEntity>>(url);
    return response;
  },

  async postNewLegalEntity(data: NewLegalEntityFormType) {
    const response = await apiClient.post<NewLegalEntityFormType>(
      `${API_ENDPOINTS.ADMIN}/legal-entities`,
      data
    );
    return response;
  },

  async getLegalEntityById(id: string): Promise<IDetailsLegalEntity> {
    const response = await apiClient.get<IDetailsLegalEntity>(
      `${API_ENDPOINTS.ADMIN}/legal-entities/${id}`
    );
    return response;
  },

  async patchLegalEntity(id: string, data: NewLegalEntityFormType) {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/legal-entities/${id}`,
      data
    );
    return response;
  },

  async postLinkChildLegalEntity(data: LinkedLegalEntityPayload) {
    const response = await apiClient.post<LinkedLegalEntityPayload>(
      `${API_ENDPOINTS.ADMIN}/legal-entities/link-child`,
      data
    );
    return response;
  },

  async postUnlinkChildLegalEntity(data: LinkedLegalEntityPayload) {
    const response = await apiClient.post<LinkedLegalEntityPayload>(
      `${API_ENDPOINTS.ADMIN}/legal-entities/unlink-child`,
      data
    );
    return response;
  },

  async deactivateLegalEntity(legalId: string) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.ADMIN}/legal-entities/${legalId}`
    );
    return response;
  },

  async getLegalEntitiesAvailableAsChildren(
    id: string
  ): Promise<LegalSubPosition[]> {
    const response =
      await apiClient.get<LegalEntitiesAvailableAsChildrenResponse>(
        `${API_ENDPOINTS.ADMIN}/legal-entities/${id}/available-as-children`
      );
    return response.items;
  },

  async getAssignableBrandsAndBranches(
    groupId: string
  ): Promise<AssignableBrandsAndBranchesResponse> {
    const response = await apiClient.get<AssignableBrandsAndBranchesResponse>(
      `${API_ENDPOINTS.ADMIN}/legal-entities/group/${groupId}/assignable`
    );
    return response;
  },

  async getAssignable(id: string): Promise<AssignableResponse> {
    const response = await apiClient.get<AssignableResponse>(
      `${API_ENDPOINTS.ADMIN}/legal-entities/${id}/assignable`
    );
    return response;
  },
};
